import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import type { Business, Profile } from '@nako/types';
import { supabase } from '@/lib/supabase';

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  business: Business | null;
  loading: boolean;
  // Definitive flag — true once we've actually finished a
  // loadProfileAndBusiness round-trip (success OR explicit "none").
  // Used by RequireAuth so we never bounce a signed-in user to /signup
  // on a slow network. Replaces the old 1200 ms grace timer hack.
  hasLoadedBusiness: boolean;

  init: () => Promise<void>;
  loadProfileAndBusiness: (userId: string) => Promise<void>;
  setBusiness: (b: Business | null) => void;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  profile: null,
  business: null,
  loading: true,
  hasLoadedBusiness: false,

  loadProfileAndBusiness: async (userId: string) => {
    // Mark "loading" again so RequireAuth shows the spinner during a
    // fresh load after sign-in / token refresh.
    set({ hasLoadedBusiness: false });
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      const { data: businesses } = await supabase
        .from('businesses')
        .select('*')
        .eq('owner_id', userId)
        .order('created_at', { ascending: true })
        .limit(1);

      set({ profile, business: businesses?.[0] ?? null });
    } finally {
      // Whether the queries succeeded or threw, the round-trip is done —
      // RequireAuth can stop waiting and route on the truth (business / no business).
      set({ hasLoadedBusiness: true });
    }
  },

  init: async () => {
    const { data } = await supabase.auth.getSession();
    set({ session: data.session, loading: false });

    if (data.session) {
      await get().loadProfileAndBusiness(data.session.user.id);
    } else {
      // No session → nothing to load, but mark loaded so unauthed pages render.
      set({ hasLoadedBusiness: true });
    }

    supabase.auth.onAuthStateChange(async (event, session) => {
      set({ session });
      if (
        session &&
        (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION')
      ) {
        await get().loadProfileAndBusiness(session.user.id);
      } else if (!session) {
        set({ profile: null, business: null, hasLoadedBusiness: true });
      }
    });
  },

  setBusiness: (b) => set({ business: b }),

  signOut: async () => {
    // Local cleanup first — never let a Supabase / network error trap the
    // user inside the app. We blow away every trace of their session
    // synchronously, THEN attempt the server signOut. If the server call
    // fails (expired JWT, offline, CORS hiccup) the user is already locally
    // signed out and the next refresh hits the unauthenticated Welcome page.
    sessionStorage.removeItem('np.adminViewAs');
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('sb-') || k.startsWith('supabase.'))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      // localStorage can be blocked in private browsing — ignore.
    }
    set({ session: null, profile: null, business: null });
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // Already cleared locally; nothing more to do.
    }
  },
}));
